import { formatCad, type Product, type ServiceProduct } from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input, Select } from "../../../core/components/ui/input";
import type { ServiceProductInput } from "../lib/api";

// Sous-section « Produits consommés » de la modal de service (admin) : une
// ligne par produit avec sa quantité par prestation. La quantité reste en
// texte tant qu'on tape (« 2,5 » accepté) ; la conversion se fait à l'envoi.

export interface DraftServiceProduct {
  key: string;
  productId: string;
  quantity: string;
}

let counter = 0;
const nextKey = () => `sp${Date.now().toString(36)}-${++counter}`;

export function draftFromServiceProduct(p: ServiceProduct): DraftServiceProduct {
  return { key: nextKey(), productId: p.productId, quantity: quantityToInput(p.quantity) };
}

export function quantityToInput(q: number): string {
  return Number.isInteger(q) ? String(q) : String(Math.round(q * 1000) / 1000);
}

export function parseQuantity(input: string): number | null {
  const n = Number(input.trim().replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Lignes prêtes pour l'API ; null si une quantité est illisible ou un produit manquant / en double. */
export function serviceProductsToInput(lines: DraftServiceProduct[]): ServiceProductInput[] | null {
  const seen = new Set<string>();
  const out: ServiceProductInput[] = [];
  for (const l of lines) {
    const quantity = parseQuantity(l.quantity);
    if (!l.productId || quantity === null || seen.has(l.productId)) return null;
    seen.add(l.productId);
    out.push({ productId: l.productId, quantity });
  }
  return out;
}

/** Coût estimé des produits d'après leur coût unitaire (admin : `unitCostCents` présent). */
export function draftProductsCostCents(lines: DraftServiceProduct[], products: Product[]): number {
  return lines.reduce((sum, l) => {
    const p = products.find((x) => x.id === l.productId);
    const q = parseQuantity(l.quantity) ?? 0;
    return sum + Math.round(q * (p?.unitCostCents ?? 0));
  }, 0);
}

export function ServiceProductsEditor({
  products,
  value,
  onChange,
}: {
  products: Product[];
  value: DraftServiceProduct[];
  onChange: (lines: DraftServiceProduct[]) => void;
}) {
  const update = (key: string, patch: Partial<DraftServiceProduct>) =>
    onChange(value.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const remove = (key: string) => onChange(value.filter((l) => l.key !== key));
  const add = () => onChange([...value, { key: nextKey(), productId: "", quantity: "1" }]);

  const unitOf = (productId: string) => products.find((p) => p.id === productId)?.unit ?? "";
  const cost = draftProductsCostCents(value, products);

  return (
    <fieldset className="atelier-subsection">
      <legend className="atelier-subsection__title">Produits consommés</legend>
      {products.length === 0 ? (
        <p className="atelier-subsection__empty">
          Aucun produit : ajoutez-en dans la page Produits pour calculer le coût de revient.
        </p>
      ) : (
        <div className="service-products">
          {value.length === 0 ? (
            <p className="atelier-subsection__empty">Aucun produit lié à ce service.</p>
          ) : (
            value.map((l) => {
              const chosen = products.find((p) => p.id === l.productId);
              return (
                <div key={l.key} className="service-products__line">
                  <Select
                    aria-label="Produit"
                    value={l.productId}
                    onChange={(e) => update(l.key, { productId: e.target.value })}
                  >
                    <option value="">Choisir un produit…</option>
                    {products
                      .filter((p) => p.active || p.id === l.productId)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.active ? "" : " (inactif)"}
                        </option>
                      ))}
                  </Select>
                  <Input
                    aria-label="Quantité"
                    inputMode="decimal"
                    value={l.quantity}
                    onChange={(e) => update(l.key, { quantity: e.target.value })}
                    placeholder="1"
                  />
                  <span className="service-products__unit">{unitOf(l.productId) || "—"}</span>
                  <span className="service-products__cost">
                    {chosen?.unitCostCents !== undefined
                      ? formatCad(
                          Math.round((parseQuantity(l.quantity) ?? 0) * chosen.unitCostCents),
                        )
                      : ""}
                  </span>
                  <Button
                    variant="ghost-danger"
                    size="sm"
                    aria-label="Retirer ce produit"
                    icon={<Icon name="close" size={14} />}
                    onClick={() => remove(l.key)}
                  >
                    Retirer
                  </Button>
                </div>
              );
            })
          )}
          <div className="service-products__foot">
            <Button variant="ghost" size="sm" icon={<Icon name="plus" size={14} />} onClick={add}>
              Ajouter un produit
            </Button>
            <span className="service-products__total">
              Produits : <strong>{formatCad(cost)}</strong>
            </span>
          </div>
        </div>
      )}
    </fieldset>
  );
}
